import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Compass, Target, Calendar, Users, Layers, Sparkles, ArrowLeft,
  CheckCircle2, Loader2, ChevronRight, Star, Plus, X,
  MessageSquare, ShieldCheck, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, Info, Zap,
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import PageHeader from '../../components/layout/PageHeader';
import { AZURE } from '../../services/api';
import { useActiveContext } from '../../contexts/ActiveContextContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWizardTracking, trackEvent } from '../../hooks/useTracking';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const STRATEGIC_LABELS = {
  pt: {
    steps: [
      'Informações Básicas', 'Objetivos & Metas',
      'Período & Fases', 'Equipe & Atletas', 'Revisão & Confirmação',
    ],
    copilotTitle: 'Copiloto de Planejamento', copilotThinking: 'Copiloto pensando...',
    copilotPlaceholder: 'Pergunte ao copiloto...',
    copilotApply: 'Aplicar', quickApplyAllPhases: 'Aplicar todas as fases',
    validationScore: (score, status) =>
      `Validação: ${score}/100 — ${status === 'approved' ? 'Aprovado ✅' : status === 'warning' ? 'Atenção ⚠️' : 'Bloqueado �ude45'}`,
    validationBlocked: 'Corrija os erros antes de publicar.',
    aiAccept: 'Aceitar', aiIgnore: 'Ignorar',
    // BasicsStep
    planName: 'Nome do Plano *', northStar: 'North Star (objetivo principal) *',
    northStarPlaceholder: 'Qual é o grande objetivo deste plano? Ex: Classificar 3 atletas para o campeonato brasileiro',
    philosophy: 'Filosofia de Treino',
    philosophyPlaceholder: 'Ex: Periodização ondulatória, base aeróbia forte',
    askAgent: 'Pedir sugestão ao agente',
    // ObjectivesStep
    objectivesHint: 'Defina os objetivos específicos e metas mensuráveis do plano.',
    addObjective: 'Adicionar Objetivo', suggestObjectives: 'Sugerir Objetivos',
    objective: 'Objetivo',
    // PeriodStep
    startDate: 'Data Início *', endDate: 'Data Fim *', durationWeeks: 'Duração (semanas)',
    phasesLabel: 'Fases do Macrociclo', phasesHint: 'Ex: Base → Específico → Competitivo → Taper',
    phasesPlaceholder: 'Base, Específico, Competitivo, Taper',
    generateCycles: 'Gerar ciclos com IA',
    // TeamStep
    teamHint: 'Após criar o plano, você poderá vincular atletas e staff específicos em "Elenco & Staff" e "Categorias".',
    categories: 'Categorias participantes', categoriesPlaceholder: 'Ex: Infantil, Juvenil, Junior, Senior',
    staff: 'Staff responsável', staffPlaceholder: 'Ex: Treinador principal, Preparador físico, Nutricionista',
    athleteCount: 'Número estimado de atletas',
    // ReviewStep
    northStarLabel: 'North Star', periodLabel: 'Período', philosophyLabel: 'Filosofia',
    objectivesLabel: 'Objetivos', categoriesLabel: 'Categorias', staffLabel: 'Staff',
    weeksUnit: 'semanas',
    // Main page
    backToDash: 'Voltar ao Dashboard', pageTitle: 'Criar Plano Estratégico',
    pageSubtitle: 'Defina o plano com ajuda do agente inteligente',
    validateBtn: 'Validar Plano com IA antes de publicar', validating: 'Validando...',
    prev: 'Anterior', next: 'Próximo', saving: 'Salvando plano...', save: 'Salvar Plano Estratégico',
    closeError: 'Fechar',
    submitError: 'Falha ao criar plano. Verifique os campos e tente novamente.',
    copilotError: 'Desculpe, não consegui processar. Tente novamente.',
    quickPrompts: {
      basics: ['Sugira um nome para o plano', 'Me ajude com o North Star', 'Qual filosofia recomendar?'],
      objectives: ['Sugira 3-5 objetivos SMART', 'Quais metas são realistas?', 'Diferença entre objetivo de performance e desenvolvimento?'],
      period: ['Gere fases do macrociclo', 'Qual duração ideal?', 'Diferença entre periodização linear e ondulatória?'],
      team: ['Quais categorias incluir?', 'Quanto staff é necessário?', 'Quantos atletas por categoria?'],
      review: ['Revise o plano completo', 'Há inconsistências?', 'Score de qualidade?'],
    },
  },
  en: {
    steps: [
      'Basic Information', 'Objectives & Goals',
      'Period & Phases', 'Team & Athletes', 'Review & Confirm',
    ],
    copilotTitle: 'Planning Copilot', copilotThinking: 'Copilot thinking...',
    copilotPlaceholder: 'Ask the copilot...',
    copilotApply: 'Apply', quickApplyAllPhases: 'Apply all phases',
    validationScore: (score, status) =>
      `Validation: ${score}/100 — ${status === 'approved' ? 'Approved ✅' : status === 'warning' ? 'Attention ⚠️' : 'Blocked �ude45'}`,
    validationBlocked: 'Fix errors before publishing.',
    aiAccept: 'Accept', aiIgnore: 'Dismiss',
    // BasicsStep
    planName: 'Plan Name *', northStar: 'North Star (main goal) *',
    northStarPlaceholder: 'What is the main goal of this plan? E.g.: Qualify 3 athletes for nationals',
    philosophy: 'Training Philosophy',
    philosophyPlaceholder: 'E.g.: Undulating periodization, strong aerobic base',
    askAgent: 'Ask agent for suggestion',
    // ObjectivesStep
    objectivesHint: 'Define the specific and measurable goals of the plan.',
    addObjective: 'Add Objective', suggestObjectives: 'Suggest Objectives',
    objective: 'Objective',
    // PeriodStep
    startDate: 'Start Date *', endDate: 'End Date *', durationWeeks: 'Duration (weeks)',
    phasesLabel: 'Macrocycle Phases', phasesHint: 'E.g.: Base → Specific → Competitive → Taper',
    phasesPlaceholder: 'Base, Specific, Competitive, Taper',
    generateCycles: 'Generate cycles with AI',
    // TeamStep
    teamHint: 'After creating the plan, you can link specific athletes and staff in "Roster & Staff" and "Categories".',
    categories: 'Participating categories', categoriesPlaceholder: 'E.g.: Youth, Junior, Senior',
    staff: 'Responsible staff', staffPlaceholder: 'E.g.: Head coach, S&C Coach, Nutritionist',
    athleteCount: 'Estimated number of athletes',
    // ReviewStep
    northStarLabel: 'North Star', periodLabel: 'Period', philosophyLabel: 'Philosophy',
    objectivesLabel: 'Objectives', categoriesLabel: 'Categories', staffLabel: 'Staff',
    weeksUnit: 'weeks',
    // Main page
    backToDash: 'Back to Dashboard', pageTitle: 'Create Strategic Plan',
    pageSubtitle: 'Define the plan with intelligent agent assistance',
    validateBtn: 'Validate Plan with AI before publishing', validating: 'Validating...',
    prev: 'Previous', next: 'Next', saving: 'Saving plan...', save: 'Save Strategic Plan',
    closeError: 'Close',
    submitError: 'Failed to create plan. Check the fields and try again.',
    copilotError: 'Sorry, I could not process that. Please try again.',
    quickPrompts: {
      basics: ['Suggest a plan name', 'Help me with the North Star', 'Which philosophy to recommend?'],
      objectives: ['Suggest 3-5 SMART objectives', 'Which goals are realistic?', 'Difference between performance and development objectives?'],
      period: ['Generate macrocycle phases', 'What is the ideal duration?', 'Difference between linear and undulating periodization?'],
      team: ['Which categories to include?', 'How much staff is needed?', 'How many athletes per category?'],
      review: ['Review the full plan', 'Any inconsistencies?', 'Quality score?'],
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Manager Strategic Plan Page — Create with AI Agent Assistance
// ═══════════════════════════════════════════════════════════════════════════════

const STEP_ICONS = [Compass, Target, Calendar, Users, CheckCircle2];
const STEP_KEYS = ['basics', 'objectives', 'period', 'team', 'review'];

function buildSteps(labels) {
  return STEP_KEYS.map((key, i) => ({ key, label: labels[i], icon: STEP_ICONS[i] }));
}

// ─── AI Copilot Panel (inline within wizard) ─────────────────────────────────

const CopilotPanel = ({ messages, loading, onSend, onAccept, onDismiss, expanded, onToggle, step, t }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
  };

  const quickPrompts = t.quickPrompts;

  return (
    <div className="border rounded-2xl overflow-hidden transition-all duration-300" style={{ borderColor: '#bfdbfe', backgroundColor: '#f0f7ff' }}>
      {/* Header — always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}>
            <Sparkles size={14} className="text-white" />
          </div>
          <span className="text-xs font-extrabold" style={{ color: '#1e3a5f' }}>{t.copilotTitle}</span>
          {loading && <Loader2 size={12} className="animate-spin text-blue-500" />}
        </div>
        {expanded ? <ChevronUp size={14} className="text-blue-400" /> : <ChevronDown size={14} className="text-blue-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Quick prompts for current step */}
          <div className="flex flex-wrap gap-1.5">
            {(quickPrompts[step] || []).map((q, i) => (
              <button
                key={i}
                onClick={() => onSend(q)}
                disabled={loading}
                className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg border hover:bg-blue-50 transition-colors disabled:opacity-50"
                style={{ borderColor: '#bfdbfe', color: '#2563eb' }}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Messages */}
          {messages.length > 0 && (
            <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
              {messages.map((msg, i) => (
                <div key={i} className={`text-xs rounded-xl p-3 ${msg.role === 'user' ? 'bg-white border border-gray-200 ml-8' : 'bg-blue-50 border border-blue-100 mr-2'}`}>
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-1 mb-1.5">
                      <Sparkles size={10} className="text-blue-600" />
                      <span className="text-[10px] font-bold text-blue-600">Copiloto</span>
                      {msg.source === 'fallback' && <span className="text-[9px] text-gray-400">(regras)</span>}
                    </div>
                  )}
                  <p className="leading-relaxed whitespace-pre-wrap" style={{ color: '#1f2937' }}>{msg.text}</p>

                  {/* Structured suggestions with accept/dismiss */}
                  {msg.suggestions && Object.keys(msg.suggestions).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {Object.entries(msg.suggestions).map(([field, value]) => (
                        <div key={field} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-blue-200">
                          <Zap size={10} className="text-amber-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-gray-500 block">{field}</span>
                            <span className="text-[10px] text-gray-700 block truncate">{typeof value === 'string' ? value : JSON.stringify(value)}</span>
                          </div>
                          <button
                            onClick={() => onAccept(field, value)}
                            className="text-[9px] font-bold px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 flex-shrink-0"
                          >
                            {t.copilotApply}
                          </button>
                          <button
                            onClick={() => onDismiss(field)}
                            className="text-[9px] font-bold px-2 py-1 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 flex-shrink-0"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Phases suggestion */}
                  {msg.phases && (
                    <div className="mt-2 space-y-1">
                      {msg.phases.map((phase, pi) => (
                        <div key={pi} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-blue-200">
                          <Layers size={10} className="text-indigo-500 flex-shrink-0" />
                          <div className="flex-1">
                            <span className="text-[10px] font-bold">{phase.name}</span>
                            <span className="text-[10px] text-gray-500 ml-1">({phase.weeks} sem)</span>
                            <span className="text-[10px] text-gray-400 block">{phase.focus}</span>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => onAccept('phases', msg.phases)}
                        className="w-full text-[10px] font-bold py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 mt-1"
                      >
                        {t.quickApplyAllPhases}
                      </button>
                    </div>
                  )}

                  {/* Validation result */}
                  {msg.validation && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck size={12} className={msg.validation.status === 'approved' ? 'text-green-600' : msg.validation.status === 'warning' ? 'text-amber-500' : 'text-red-500'} />
                        <span className="text-[10px] font-extrabold">
                          Score: {msg.validation.quality_score}/100 — {msg.validation.status === 'approved' ? '✅ Aprovado' : msg.validation.status === 'warning' ? '⚠️ Atenção' : '🚫 Bloqueado'}
                        </span>
                      </div>
                      {msg.validation.issues?.map((issue, ii) => (
                        <div key={ii} className="flex items-start gap-1.5 text-[10px]">
                          {issue.severity === 'error' ? <X size={10} className="text-red-500 mt-0.5" /> :
                           issue.severity === 'warning' ? <AlertTriangle size={10} className="text-amber-500 mt-0.5" /> :
                           <Info size={10} className="text-blue-400 mt-0.5" />}
                          <span><strong>{issue.field}:</strong> {issue.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={t.copilotPlaceholder}
              disabled={loading}
              className="flex-1 px-3 py-2 text-xs rounded-xl border bg-white disabled:opacity-50"
              style={{ borderColor: '#bfdbfe' }}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="px-3 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
            >
              <MessageSquare size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Validation Banner ───────────────────────────────────────────────────────

const ValidationBanner = ({ validation, onFix, t }) => {
  if (!validation) return null;
  const { status, quality_score, issues } = validation;
  const bg = status === 'approved' ? '#f0fdf4' : status === 'warning' ? '#fffbeb' : '#fef2f2';
  const border = status === 'approved' ? '#bbf7d0' : status === 'warning' ? '#fde68a' : '#fecaca';
  const color = status === 'approved' ? '#166534' : status === 'warning' ? '#92400e' : '#991b1b';

  return (
    <div className="p-4 rounded-xl border mb-4" style={{ backgroundColor: bg, borderColor: border }}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} style={{ color }} />
        <span className="text-sm font-extrabold" style={{ color }}>
          {t.validationScore(quality_score, status)}
        </span>
      </div>
      {issues?.filter(i => i.severity !== 'info').map((issue, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs mb-1" style={{ color }}>
          {issue.severity === 'error' ? <X size={10} className="mt-0.5 flex-shrink-0" /> : <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />}
          <span><strong>{issue.field}:</strong> {issue.message}</span>
        </div>
      ))}
      {status === 'blocked' && (
        <p className="text-[10px] mt-2" style={{ color }}>{t.validationBlocked}</p>
      )}
    </div>
  );
};

// ─── AI Suggestion Chip (kept for backward compat) ──────────────────────────

const AISuggestion = ({ text, onAccept, onDismiss, t }) => (
  <div className="flex items-start gap-2 p-3 rounded-xl border" style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}>
    <Sparkles size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#2563eb' }} />
    <div className="flex-1">
      <p className="text-xs leading-relaxed" style={{ color: '#1e40af' }}>{text}</p>
      <div className="flex gap-2 mt-2">
        <button onClick={onAccept} className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
          {t.aiAccept}
        </button>
        <button onClick={onDismiss} className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-white border border-gray-200 hover:bg-gray-50" style={{ color: VUKZ.textLight }}>
          {t.aiIgnore}
        </button>
      </div>
    </div>
  </div>
);

// ─── Step Components ─────────────────────────────────────────────────────────

const BasicsStep = ({ form, setField, suggestion, askAgent, t }) => (
  <div className="space-y-5">
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.planName}</label>
      <input
        type="text" required value={form.plan_title}
        onChange={(e) => setField('plan_title', e.target.value)}
        placeholder="Ex: Plano de Performance 2026 - Temporada Curta"
        className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}
      />
    </div>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.northStar}</label>
      <textarea
        value={form.north_star}
        onChange={(e) => setField('north_star', e.target.value)}
        placeholder={t.northStarPlaceholder}
        rows={3}
        className="w-full px-4 py-3 rounded-xl border text-sm resize-none" style={{ borderColor: VUKZ.border }}
      />
      {!form.north_star && (
        <button onClick={() => askAgent('north_star')} className="mt-2 flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-700">
          <Sparkles size={12} /> {t.askAgent}
        </button>
      )}
    </div>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.philosophy}</label>
      <input
        type="text" value={form.philosophy || ''}
        onChange={(e) => setField('philosophy', e.target.value)}
        placeholder={t.philosophyPlaceholder}
        className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}
      />
    </div>
    {suggestion && <AISuggestion text={suggestion.text} onAccept={() => { setField(suggestion.field, suggestion.value); }} onDismiss={() => {}} t={t} />}
  </div>
);

const ObjectivesStep = ({ form, setField, askAgent, t }) => {
  const addObjective = () => setField('objectives', [...(form.objectives || []), '']);
  const updateObj = (i, val) => {
    const objs = [...(form.objectives || [])];
    objs[i] = val;
    setField('objectives', objs);
  };
  const removeObj = (i) => setField('objectives', (form.objectives || []).filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      <p className="text-xs" style={{ color: VUKZ.textLight }}>{t.objectivesHint}</p>
      {(form.objectives || []).map((obj, i) => (
        <div key={i} className="flex gap-2">
          <input
            type="text" value={obj}
            onChange={(e) => updateObj(i, e.target.value)}
            placeholder={`${t.objective} ${i + 1}`}
            className="flex-1 px-4 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}
          />
          <button onClick={() => removeObj(i)} className="p-2 rounded-lg hover:bg-gray-100"><X size={14} /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={addObjective} className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg border hover:bg-gray-50" style={{ borderColor: VUKZ.border, color: VUKZ.primary }}>
          <Plus size={12} /> {t.addObjective}
        </button>
        <button onClick={() => askAgent('objectives')} className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg text-blue-600 hover:bg-blue-50">
          <Sparkles size={12} /> {t.suggestObjectives}
        </button>
      </div>
    </div>
  );
};

const PeriodStep = ({ form, setField, onGenerateCycles, agentLoading, t }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.startDate}</label>
        <input type="date" value={form.period_start || ''} onChange={(e) => setField('period_start', e.target.value)} className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
      </div>
      <div>
        <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.endDate}</label>
        <input type="date" value={form.period_end || ''} onChange={(e) => setField('period_end', e.target.value)} className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
      </div>
    </div>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.durationWeeks}</label>
      <input type="number" value={form.duration_weeks || ''} onChange={(e) => setField('duration_weeks', parseInt(e.target.value) || '')} placeholder="Ex: 16" className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
    </div>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.phasesLabel}</label>
      <p className="text-[11px] mb-2" style={{ color: VUKZ.textLight }}>{t.phasesHint}</p>
      <input type="text" value={form.phases_text || ''} onChange={(e) => setField('phases_text', e.target.value)} placeholder={t.phasesPlaceholder} className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
      <button onClick={onGenerateCycles} disabled={agentLoading} className="mt-2 flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 disabled:opacity-50">
        <RefreshCw size={12} className={agentLoading ? 'animate-spin' : ''} /> {t.generateCycles}
      </button>
    </div>
  </div>
);

const TeamStep = ({ form, setField, t }) => (
  <div className="space-y-5">
    <p className="text-xs" style={{ color: VUKZ.textLight }}>{t.teamHint}</p>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.categories}</label>
      <input type="text" value={form.categories_text || ''} onChange={(e) => setField('categories_text', e.target.value)} placeholder={t.categoriesPlaceholder} className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
    </div>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.staff}</label>
      <input type="text" value={form.staff_text || ''} onChange={(e) => setField('staff_text', e.target.value)} placeholder={t.staffPlaceholder} className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
    </div>
    <div>
      <label className="text-xs font-bold block mb-1.5" style={{ color: VUKZ.textLight }}>{t.athleteCount}</label>
      <input type="number" value={form.athlete_count || ''} onChange={(e) => setField('athlete_count', parseInt(e.target.value) || '')} placeholder="Ex: 25" className="w-full px-4 py-3 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
    </div>
  </div>
);

const ReviewStep = ({ form, t }) => (
  <div className="space-y-4">
    <div className="p-4 rounded-xl" style={{ backgroundColor: VUKZ.primaryLight }}>
      <div className="flex items-center gap-2 mb-3">
        <Star size={16} style={{ color: VUKZ.primary }} />
        <h3 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{form.plan_title || '—'}</h3>
      </div>
      <p className="text-xs mb-2" style={{ color: VUKZ.text }}><strong>{t.northStarLabel}:</strong> {form.north_star || '—'}</p>
      <p className="text-xs mb-2" style={{ color: VUKZ.text }}><strong>{t.periodLabel}:</strong> {form.period_start || '?'} → {form.period_end || '?'} ({form.duration_weeks || '?'} {t.weeksUnit})</p>
      <p className="text-xs mb-2" style={{ color: VUKZ.text }}><strong>{t.philosophyLabel}:</strong> {form.philosophy || '—'}</p>
      {form.objectives?.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-bold" style={{ color: VUKZ.text }}>{t.objectivesLabel}:</p>
          <ul className="list-disc list-inside text-xs mt-1" style={{ color: VUKZ.text }}>
            {form.objectives.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </div>
      )}
      {form.categories_text && <p className="text-xs mt-2" style={{ color: VUKZ.text }}><strong>{t.categoriesLabel}:</strong> {form.categories_text}</p>}
      {form.staff_text && <p className="text-xs mt-2" style={{ color: VUKZ.text }}><strong>{t.staffLabel}:</strong> {form.staff_text}</p>}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════

const ManagerStrategicPlanPage = () => {
  const navigate = useNavigate();
  const { clubId } = useActiveContext();
  const { user } = useAuth();
  const { lang } = useGovernanceLang();
  const t = STRATEGIC_LABELS[lang] || STRATEGIC_LABELS.pt;
  const STEPS = buildSteps(t.steps);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [copilotExpanded, setCopilotExpanded] = useState(false);
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [validation, setValidation] = useState(null);
  const { trackStep, trackComplete } = useWizardTracking('strategic_plan', STEPS.length);
  const messagesEndRef = useRef(null);
  const [form, setForm] = useState({
    plan_title: '',
    north_star: '',
    philosophy: '',
    period_start: '',
    period_end: '',
    duration_weeks: '',
    phases_text: '',
    objectives: [],
    categories_text: '',
    staff_text: '',
    athlete_count: '',
  });

  const setField = useCallback((key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setSuggestion(null);
    setValidation(null); // Reset validation when form changes
  }, []);

  // ─── Copilot: Send message to planning agent ────────────────────────────

  const sendToCopilot = async (message) => {
    setCopilotExpanded(true);
    setAgentLoading(true);
    const stepKey = STEPS[step]?.key || 'basics';

    // Add user message
    setCopilotMessages(prev => [...prev, { role: 'user', text: message }]);

    try {
      const { data } = await AZURE.post('/gestor/agent/planning/assist', {
        message,
        step: stepKey,
        form_state: form,
        club_id: clubId,
        user_id: user?.id,
      });

      const result = data?.data || {};
      const agentMsg = {
        role: 'assistant',
        text: result.text || 'Sem resposta do agente.',
        source: result.source || 'unknown',
      };

      // Parse structured data into message
      if (result.type === 'structured' && result.data) {
        if (result.data.suggestions) agentMsg.suggestions = result.data.suggestions;
        if (result.data.objectives) agentMsg.suggestions = { objectives: result.data.objectives };
        if (result.data.phases) agentMsg.phases = result.data.phases;
        if (result.data.quality_score !== undefined) agentMsg.validation = result.data;
      }

      setCopilotMessages(prev => [...prev, agentMsg]);
      trackEvent('plan_copilot_response', { step: stepKey, source: result.source });
    } catch (err) {
      console.error('Copilot error:', err);
      setCopilotMessages(prev => [...prev, {
        role: 'assistant', text: t.copilotError, source: 'error',
      }]);
    } finally {
      setAgentLoading(false);
    }
  };

  // ─── Copilot: Accept/Dismiss suggestions ──────────────────────────────

  const acceptSuggestion = (field, value) => {
    if (field === 'objectives' && Array.isArray(value)) {
      setField('objectives', value);
    } else if (field === 'phases' && Array.isArray(value)) {
      const phasesText = value.map(p => p.name).join(', ');
      setField('phases_text', phasesText);
      // Also store structured phases for later
      setForm(f => ({ ...f, _phases_structured: value }));
    } else {
      setField(field, value);
    }
    trackEvent('plan_copilot_suggestion_accepted', { field });

    // Log to backend
    AZURE.post('/gestor/agent/planning/apply-suggestions', {
      action: 'accepted', field, suggested_value: value,
      step: STEPS[step]?.key, club_id: clubId, user_id: user?.id,
    }).catch(() => {});
  };

  const dismissSuggestion = (field) => {
    trackEvent('plan_copilot_suggestion_rejected', { field });
    AZURE.post('/gestor/agent/planning/apply-suggestions', {
      action: 'rejected', field,
      step: STEPS[step]?.key, club_id: clubId, user_id: user?.id,
    }).catch(() => {});
  };

  // ─── AI Agent Assistance (legacy — per-field) ──────────────────────────

  const askAgent = async (field) => {
    const promptMap = {
      north_star: 'Sugira um North Star para este plano de natação.',
      objectives: 'Sugira 3-5 objetivos específicos e mensuráveis.',
    };
    sendToCopilot(promptMap[field] || `Ajude com o campo "${field}"`);
  };

  // ─── Pre-publish validation ────────────────────────────────────────────

  const validateBeforePublish = async () => {
    setAgentLoading(true);
    try {
      const { data } = await AZURE.post('/gestor/agent/planning/validate', {
        form_state: form,
        club_id: clubId,
        user_id: user?.id,
      });
      const result = data?.data?.data || data?.data || {};
      setValidation(result);

      // Also show in copilot
      setCopilotExpanded(true);
      setCopilotMessages(prev => [...prev, {
        role: 'assistant',
        text: `Validação completa: Score ${result.quality_score || '?'}/100`,
        source: result.source || 'rules',
        validation: result,
      }]);
    } catch (err) {
      console.error('Validation error:', err);
    } finally {
      setAgentLoading(false);
    }
  };

  // ─── Generate Cycles ──────────────────────────────────────────────────

  const generateCycles = async () => {
    setAgentLoading(true);
    setCopilotExpanded(true);
    try {
      const { data } = await AZURE.post('/gestor/agent/planning/generate-cycles', {
        form_state: form,
        club_id: clubId,
        user_id: user?.id,
      });
      const result = data?.data?.data || data?.data || {};
      if (result.phases) {
        setCopilotMessages(prev => [...prev, {
          role: 'assistant',
          text: result.rationale || `${result.phases.length} fases geradas.`,
          source: 'foundry',
          phases: result.phases,
        }]);
      }
    } catch (err) {
      console.error('Cycle generation error:', err);
    } finally {
      setAgentLoading(false);
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!form.plan_title || !form.period_start || !form.period_end || !clubId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Build phases array from text input
      const phasesArray = form.phases_text
        ? form.phases_text.split(',').map((p, i) => ({ name: p.trim(), order: i + 1 }))
        : null;

      // 1. Create strategic plan (gestor module → strategic_plans table)
      const strategicPayload = {
        tenant_id: clubId,
        sport: 'swimming',
        title: form.plan_title,
        north_star: form.north_star,
        period_start: form.period_start,
        period_end: form.period_end,
        phases: phasesArray,
        global_kpis: form.objectives?.length > 0
          ? form.objectives.map((o, i) => ({ label: o, target: null, order: i + 1 }))
          : null,
        created_by: user?.name || 'Gestor',
      };
      const strategicRes = await AZURE.post('/gestor/plans', strategicPayload);
      const strategicPlanId = strategicRes?.data?.data?.plan_id;

      // 2. Also create club_plan (performance_planning → club_plans table) for staff/athlete visibility
      const clubPlanPayload = {
        club_id: clubId,
        plan_title: form.plan_title,
        north_star: form.north_star,
        philosophy: form.philosophy,
        period_start: form.period_start,
        period_end: form.period_end,
        duration_weeks: form.duration_weeks || 16,
        methodology_version: '1.0',
        objectives: form.objectives,
        categories_text: form.categories_text,
        staff_text: form.staff_text,
        strategic_plan_id: strategicPlanId || null,
      };
      await AZURE.post('/planning/club-plans', clubPlanPayload);

      trackComplete({ club_id: clubId, has_objectives: form.objectives?.length > 0 });
      navigate('/manager/plan', { state: { planCreated: true } });
    } catch (err) {
      console.error('Error creating plan:', err);
      setSubmitError(t.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const canAdvance = () => {
    if (step === 0) return !!form.plan_title;
    if (step === 1) return true;
    if (step === 2) return !!form.period_start && !!form.period_end;
    return true;
  };

  const currentStep = STEPS[step];

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-3xl mx-auto px-4 lg:px-6 py-6">

        {/* Back */}
        <button onClick={() => navigate('/manager')} className="flex items-center gap-1 text-xs font-bold mb-4 hover:opacity-70" style={{ color: VUKZ.textLight }}>
          <ArrowLeft size={14} /> {t.backToDash}
        </button>

        <PageHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
          badgeText="NOVO"
          className="mb-6"
        />

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-1">
              <div
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold whitespace-nowrap ${i <= step ? '' : 'opacity-40'}`}
                style={{
                  backgroundColor: i === step ? VUKZ.primary : i < step ? VUKZ.primaryLight : '#f3f4f6',
                  color: i === step ? 'white' : VUKZ.text,
                }}
              >
                <s.icon size={12} />
                {s.label}
              </div>
              {i < STEPS.length - 1 && <ChevronRight size={12} className="opacity-30" />}
            </div>
          ))}
        </div>

        {/* Agent loading indicator */}
        {agentLoading && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-xl" style={{ backgroundColor: '#eff6ff' }}>
            <Loader2 size={14} className="animate-spin text-blue-600" />
            <span className="text-xs font-bold text-blue-600">{t.copilotThinking}</span>
          </div>
        )}

        {/* Copilot Panel — always visible, collapsible */}
        <div className="mb-4">
          <CopilotPanel
            messages={copilotMessages}
            loading={agentLoading}
            onSend={sendToCopilot}
            onAccept={acceptSuggestion}
            onDismiss={dismissSuggestion}
            expanded={copilotExpanded}
            onToggle={() => setCopilotExpanded(e => !e)}
            step={STEPS[step]?.key || 'basics'}
            t={t}
          />
        </div>

        {/* Validation banner (review step) */}
        {step === 4 && <ValidationBanner validation={validation} t={t} />}

        {/* Step content */}
        <div className="bg-white rounded-2xl border p-6 mb-6" style={{ borderColor: VUKZ.border }}>
          {step === 0 && <BasicsStep form={form} setField={setField} suggestion={suggestion} askAgent={askAgent} t={t} />}
          {step === 1 && <ObjectivesStep form={form} setField={setField} askAgent={askAgent} t={t} />}
          {step === 2 && <PeriodStep form={form} setField={setField} onGenerateCycles={generateCycles} agentLoading={agentLoading} t={t} />}
          {step === 3 && <TeamStep form={form} setField={setField} t={t} />}
          {step === 4 && (
            <>
              <ReviewStep form={form} t={t} />
              {!validation && (
                <button
                  onClick={validateBeforePublish}
                  disabled={agentLoading}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold border-2 hover:bg-indigo-50 disabled:opacity-50 transition-colors"
                  style={{ borderColor: '#818cf8', color: '#4338ca' }}
                >
                  <ShieldCheck size={14} />
                  {agentLoading ? t.validating : t.validateBtn}
                </button>
              )}
            </>
          )}
        </div>

        {/* Inline error feedback */}
        {submitError && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-xl border" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
            <X size={14} className="flex-shrink-0" style={{ color: '#dc2626' }} />
            <span className="text-xs font-bold" style={{ color: '#991b1b' }}>{submitError}</span>
            <button onClick={() => setSubmitError(null)} className="ml-auto text-xs underline" style={{ color: '#991b1b' }}>{t.closeError}</button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="px-5 py-2.5 rounded-xl text-xs font-bold border hover:bg-gray-50 disabled:opacity-30"
            style={{ borderColor: VUKZ.border, color: VUKZ.text }}
          >
            {t.prev}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => { setStep(s => { const next = s + 1; trackStep(next, STEPS[next]?.key); return next; }); }}
              disabled={!canAdvance()}
              className="px-5 py-2.5 rounded-xl text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
              style={{ background: VUKZ_GRADIENT }}
            >
              {t.next} <ChevronRight size={14} className="inline" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !form.plan_title}
              className="px-6 py-2.5 rounded-xl text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
              style={{ background: VUKZ_GRADIENT }}
            >
              {submitting ? t.saving : t.save}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default ManagerStrategicPlanPage;
